/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {WalkCard} from 'UI/components/modules/WalkCard';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {useWalksPage} from 'UI/hooks/useWalksPage';
import {AppLayout} from 'UI/layouts/AppLayout';

import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildBreadcrumbJsonLd, buildCollectionPageJsonLd, buildWalkListJsonLd, resolveImageUrl} from 'UI/utils/jsonLd';

import type {GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {City, Config, LegalPage, Walk} from 'types/payload-types';

interface WalksPageProps {
    cities: City[];
    legalPages: LegalPage[];
    reviewsByWalkId: Record<string, {rating: number}[]>;
    walks: Walk[];
}

/**
 * Walks-Übersichtsseite. Listet alle veröffentlichten Walks mit Suche und Stadtfilter.
 *
 * @param props                 Die Seiten-Props.
 * @param props.walks           Array aller veröffentlichten Walks.
 * @param props.cities          Array aller Städte für den Filter.
 * @param props.reviewsByWalkId Freigegebene Bewertungen, gruppiert nach Walk-ID.
 * @returns ReactNode der Walks-Übersichtsseite.
 */
const WalksPage: NextSSGPageWithLayout<typeof getStaticProps> = (
    {reviewsByWalkId, walks}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {filteredWalks, handleSearch, search, t} = useWalksPage(walks);

    return (
        <Content data-cy="WalksPage">
            <SeoHead
                description={t('walks.metaDescription')}
                image={resolveImageUrl(typeof walks[0]?.coverImage === 'object' ? walks[0].coverImage : null)}
                jsonLd={[
                    buildWalkListJsonLd(walks, reviewsByWalkId),
                    buildCollectionPageJsonLd(
                        {
                            name: t('walks.title'),
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/walks`
                        },
                        `${process.env.NEXT_PUBLIC_BASE_URL}/walks#itemlist`
                    ),
                    buildBreadcrumbJsonLd([
                        {
                            name: 'Home',
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                        },
                        {
                            name: t('walks.title'),
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/walks`
                        }
                    ])
                ]}
                title={t('walks.title')}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/walks`}
            />
            <PageTitle>{t('walks.title')}</PageTitle>
            <FilterBar>
                <SearchInput
                    data-cy="WalksPage-search"
                    placeholder={t('walks.searchPlaceholder')}
                    type="text"
                    value={search}
                    onChange={handleSearch}
                />
            </FilterBar>
            <WalkGrid>
                {filteredWalks.map(walk => (
                    <WalkCard key={walk.id} testId="WalksPage-card" walk={walk} />
                ))}
                {filteredWalks.length === 0 && <NoResults>{t('walks.noResults')}</NoResults>}
            </WalkGrid>
        </Content>
    );
};

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout (Header + Navigation).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
WalksPage.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="home" legalPages={pageProps.legalPages} isBackVisible>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
WalksPage.getLayoutKey = () => 'walks';

export default WalksPage;

// ── Styled ──────────────────────────────────────────────────

const Content = styled.div`
    padding: 0 ${spacing(6)};
`;

const PageTitle = styled.h1`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 3.2rem;
    margin: ${spacing(8)} 0 ${spacing(6)};
`;

const FilterBar = styled.div`
    display: flex;
    gap: ${spacing(4)};
    margin-bottom: ${spacing(8)};
`;

const SearchInput = styled.input`
    border: 1px solid ${({theme}) => theme.colors.inkDisabled};
    border-radius: 8px;
    flex: 1;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    padding: ${spacing(3)} ${spacing(4)};
`;

const WalkGrid = styled.div`
    display: grid;
    gap: ${spacing(6)};
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
`;

const NoResults = styled.p`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    grid-column: 1 / -1;
    text-align: center;
`;

/**
 * Lädt alle veröffentlichten Walks und Städte.
 *
 * @param props                  Die Static-Props Kontext-Optionen.
 * @param props.locale           Die aktuelle Locale.
 * @param props.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit Walks- und Cities-Arrays.
 */
export const getStaticProps: GetStaticProps<WalksPageProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {
                cities: [],
                legalPages: [],
                reviewsByWalkId: {},
                walks: []
            },
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [walksResult, citiesResult, reviewsResult, legalPages] = await Promise.all([
        payload.find({
            collection: 'walks',
            depth: 1,
            joins: {waypoints: false},
            limit: 1000,
            locale: payloadLocale,
            select: {
                categories: true,
                city: true,
                coverImage: true,
                gallery: true,
                general: true,
                meta: true,
                pricing: true,
                shortDescription: true,
                slug: true,
                title: true
            },
            where: {_status: {equals: 'published'}}
        }),
        payload.find({
            collection: 'cities',
            depth: 0,
            limit: 100,
            locale: payloadLocale
        }),
        payload.find({
            collection: 'reviews',
            depth: 0,
            limit: 0,
            overrideAccess: true,
            select: {
                rating: true,
                walk: true
            },
            where: {status: {equals: 'approved'}}
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    const leanWalks = walksResult.docs as unknown as Walk[];

    const reviewsByWalkId: Record<string, {rating: number}[]> = {};

    for (const review of reviewsResult.docs) {
        const walkId = typeof review.walk === 'object' ? review.walk.id : review.walk;

        (reviewsByWalkId[walkId] ??= []).push({rating: review.rating});
    }

    return {
        props: {
            cities: JSON.parse(JSON.stringify(citiesResult.docs)) as City[],
            legalPages,
            reviewsByWalkId: JSON.parse(JSON.stringify(reviewsByWalkId)) as Record<string, {rating: number}[]>,
            walks: JSON.parse(JSON.stringify(leanWalks)) as Walk[]
        },
        revalidate: 18000
    };
};